import { EmailTriggerType, EmailVariableCategory } from "@/types/email";

/**
 * Email system constants
 */

// Email status mappings
export const EMAIL_STATUS = {
  PENDING: "pending",
  SENT: "sent",
  FAILED: "failed",
  BOUNCED: "bounced",
  DELIVERED: "delivered",
} as const;

// Default email settings per trigger type
export const EMAIL_DEFAULTS: Record<
  EmailTriggerType,
  {
    tag: string;
    priority: "high" | "normal" | "low";
    trackOpens: boolean;
    trackClicks: boolean;
  }
> = {
  booking_confirmation: {
    tag: "booking",
    priority: "high",
    trackOpens: true,
    trackClicks: true,
  },
  booking_preliminary: {
    tag: "booking",
    priority: "high",
    trackOpens: true,
    trackClicks: true,
  },
  booking_confirmed_corporate: {
    tag: "booking",
    priority: "high",
    trackOpens: true,
    trackClicks: true,
  },
  booking_confirmed_private: {
    tag: "booking",
    priority: "high",
    trackOpens: true,
    trackClicks: true,
  },
  booking_confirmed_roadshow: {
    tag: "booking",
    priority: "high",
    trackOpens: true,
    trackClicks: true,
  },
  booking_updated_minor: {
    tag: "update",
    priority: "normal",
    trackOpens: true,
    trackClicks: true,
  },
  booking_updated_major: {
    tag: "update",
    priority: "high",
    trackOpens: true,
    trackClicks: true,
  },
  updated_small_changes: {
    tag: "update",
    priority: "normal",
    trackOpens: true,
    trackClicks: true,
  },
  updated_time_change: {
    tag: "update",
    priority: "high",
    trackOpens: true,
    trackClicks: true,
  },
  reminder_tips_corporate: {
    tag: "reminder",
    priority: "normal",
    trackOpens: true,
    trackClicks: true,
  },
  reminder_tips_private: {
    tag: "reminder",
    priority: "normal",
    trackOpens: true,
    trackClicks: true,
  },
  reminder_final: {
    tag: "reminder",
    priority: "high",
    trackOpens: true,
    trackClicks: true,
  },
  thanks_for_visiting: {
    tag: "follow-up",
    priority: "low",
    trackOpens: true,
    trackClicks: true,
  },
  addon_special_questions: {
    tag: "booking",
    priority: "normal",
    trackOpens: true,
    trackClicks: false,
  },

  // Database trigger types (actual templates in DB)
  reserved_corporate: {
    tag: "booking",
    priority: "high",
    trackOpens: true,
    trackClicks: true,
  },
  reserved_private: {
    tag: "booking",
    priority: "high",
    trackOpens: true,
    trackClicks: true,
  },
  reserved_roadshow: {
    tag: "booking",
    priority: "high",
    trackOpens: true,
    trackClicks: true,
  },
  preliminary_reservation: {
    tag: "booking",
    priority: "high",
    trackOpens: true,
    trackClicks: true,
  },

  // Database reminder types
  reminder_1d_corporate: {
    tag: "reminder",
    priority: "high",
    trackOpens: true,
    trackClicks: true,
  },
  reminder_1d_private: {
    tag: "reminder",
    priority: "high",
    trackOpens: true,
    trackClicks: true,
  },
  reminder_3d_corporate: {
    tag: "reminder",
    priority: "normal",
    trackOpens: true,
    trackClicks: true,
  },
  reminder_3d_private: {
    tag: "reminder",
    priority: "normal",
    trackOpens: true,
    trackClicks: true,
  },
  reminder_7d: {
    tag: "reminder",
    priority: "normal",
    trackOpens: true,
    trackClicks: true,
  },
  reminder_24h: {
    tag: "reminder",
    priority: "high",
    trackOpens: true,
    trackClicks: true,
  },
  reminder_2h: {
    tag: "reminder",
    priority: "high",
    trackOpens: true,
    trackClicks: true,
  },

  // Cancellation email
  booking_cancellation: {
    tag: "cancellation",
    priority: "high",
    trackOpens: true,
    trackClicks: false,
  },

  // Invoice reminder
  invoice_info_reminder: {
    tag: "reminder",
    priority: "high",
    trackOpens: true,
    trackClicks: true,
  },

  // Road show specific emails
  booking_modification_roadshow: {
    tag: "update",
    priority: "high",
    trackOpens: true,
    trackClicks: true,
  },
  reminder_3d_roadshow: {
    tag: "reminder",
    priority: "normal",
    trackOpens: true,
    trackClicks: true,
  },
  reminder_1d_roadshow: {
    tag: "reminder",
    priority: "high",
    trackOpens: true,
    trackClicks: true,
  },
  post_show_roadshow: {
    tag: "follow-up",
    priority: "low",
    trackOpens: true,
    trackClicks: true,
  },
  cancellation_roadshow: {
    tag: "cancellation",
    priority: "high",
    trackOpens: true,
    trackClicks: false,
  },
};

// Language suffixes for multi-language templates
export const LANGUAGE_SUFFIXES = {
  EN: "_en",
  SV: "_sv",
  NO: "_no",
  DA: "_da",
} as const;

// Default language fallback chain
export const LANGUAGE_FALLBACK = {
  sv: ["sv", "en"],
  no: ["no", "en"],
  da: ["da", "en"],
  en: ["en"],
} as const;

// Email variable categories with display names
export const VARIABLE_CATEGORIES: Record<
  EmailVariableCategory,
  {
    name: string;
    description: string;
    icon: string;
  }
> = {
  customer: {
    name: "Customer",
    description: "Customer information variables",
    icon: "👤",
  },
  booking: {
    name: "Booking",
    description: "Booking details and status",
    icon: "📅",
  },
  show: {
    name: "Show",
    description: "Show and performance information",
    icon: "🎭",
  },
  venue: {
    name: "Venue",
    description: "Venue location and contact",
    icon: "🏢",
  },
  financial: {
    name: "Financial",
    description: "Pricing and payment information",
    icon: "💰",
  },
  system: {
    name: "System",
    description: "Links and system-generated content",
    icon: "⚙️",
  },
};

// Common variable mappings for quick reference
export const COMMON_VARIABLES = [
  { name: "customer_name", category: "customer", required: true },
  { name: "booking_number", category: "booking", required: true },
  { name: "booking_date", category: "booking", required: true },
  { name: "booking_time", category: "booking", required: true },
  { name: "show_names", category: "show", required: true },
  { name: "venue_name", category: "venue", required: true },
  { name: "total_price", category: "financial", required: true },
  { name: "portal_link", category: "system", required: true },
];

// Fields that trigger major vs minor updates
export const UPDATE_TRIGGER_FIELDS = {
  MAJOR: ["date", "start_time", "end_time", "venue_id", "show_id"],
  MINOR: [
    "participants",
    "add_ons",
    "language",
    "special_requests",
    "internal_notes",
  ],
};

// Email timing rules (in hours)
export const EMAIL_TIMING = {
  POST_VISIT_DELAY: 15, // Send review request 15 hours after event
  REMINDER_TIPS_DAYS: 3, // Send tips 3 days after booking
  REMINDER_FINAL_DAYS: 7, // Send final reminder 7 days before event
  PRELIMINARY_EXPIRY_HOURS: 48, // Preliminary bookings expire after 48 hours
};

// Maximum retry attempts for failed emails
export const EMAIL_RETRY_LIMITS = {
  MAX_ATTEMPTS: 3,
  RETRY_DELAY_MINUTES: [5, 30, 120], // Exponential backoff
};

// Email preview sample data
export const PREVIEW_SAMPLE_DATA = {
  customer_name: "Joakim",
  customer_email: "joakim@example.com",
  customer_phone: "+46701234567",
  company_name: "Acme Corporation",
  booking_number: "ONR-2025-001",
  booking_date: "Thursday, October 26, 2025",
  booking_time: "18:30",
  participants: "15",
  booking_type: "corporate",
  show_names: "The Switcharoo",
  show_duration: "90 minutes",
  min_charge: "8",
  venue_name: "On Air Stockholm",
  venue_address: "Kungsgatan 45, Stockholm",
  venue_phone: "08-446 65 61",
  venue_email: "stockholm@onairgameshows.com",
  total_price: "4,500 kr",
  total_amount_inc_vat: "4,500 kr",
  currency: "kr",
  portal_link: "https://onair.example.com/portal/booking/123",
  cancel_link: "https://onair.example.com/portal/booking/123/cancel",
  review_link: "https://forms.gle/example",
  survey_link: "https://onair.example.com/review/1?bookingId=123&mode=survey",
  cancellation_policy:
    "Cancellation at least 21 days before: Free of charge. Later cancellation: Full amount will be invoiced.",
};
